import React, { useState, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Alert,
  AlertIcon,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Divider,
  Badge,
  Grid,
  GridItem,
  Image,
  Text as ChakraText,
  Spinner,
  Center
} from '@chakra-ui/react';
import { FaUpload, FaEdit, FaEye, FaFax, FaDownload, FaTrash } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import faxService from '../../utils/faxService';

export default function FaxEditor({ config }) {
  const [formData, setFormData] = useState({
    title: '',
    subject: config?.default_subject || '',
    comments: config?.default_comments || '',
    cover_page: config?.default_cover_page || 'Standard',
    recipient_name: '',
    organization: '',
    to_fax_number: '',
    template_type: 'custom'
  });

  const [files, setFiles] = useState([]);
  const [previewMode, setPreviewMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const fileInputRef = useRef(null);
  const toast = useToast();

  React.useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('fax_templates')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error loading templates',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Validate file types
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    const invalidFiles = selectedFiles.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      toast({
        title: 'Invalid file type',
        description: `The following files are not supported: ${invalidFiles.map(f => f.name).join(', ')}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const loadTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      title: template.title,
      subject: template.subject,
      comments: template.comments,
      cover_page: template.cover_page,
      recipient_name: template.recipient_name,
      organization: template.organization,
      to_fax_number: template.to_fax_number,
      template_type: 'template'
    });
    
    // Load template files if any
    if (template.files && template.files.length > 0) {
      // Note: In a real implementation, you'd need to download these files
      // For now, we'll show a placeholder
      toast({
        title: 'Template loaded',
        description: `Loaded template "${template.title}" with ${template.files.length} files`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const saveTemplate = async () => {
    if (!formData.title) {
      toast({
        title: 'Template name required',
        description: 'Please enter a name for the template',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const templateData = {
        title: formData.title,
        subject: formData.subject,
        comments: formData.comments,
        cover_page: formData.cover_page,
        recipient_name: formData.recipient_name,
        organization: formData.organization,
        to_fax_number: formData.to_fax_number,
        files: files.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type
        })),
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('fax_templates')
        .upsert(templateData)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Template saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast({
        title: 'Error saving template',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSendFax = async () => {
    if (files.length === 0) {
      toast({
        title: 'No files selected',
        description: 'Please add at least one file to send',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.to_fax_number) {
      toast({
        title: 'Recipient required',
        description: 'Please enter a recipient fax number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSending(true);
    try {
      // For now, send the first file
      // In a full implementation, you'd handle multiple files
      const file = files[0];
      
      const result = await faxService.sendFax({
        toFaxNumber: formData.to_fax_number,
        file,
        fileName: file.name,
        subject: formData.subject,
        comments: formData.comments,
        recipientName: formData.recipient_name,
        organization: formData.organization,
        coverPage: formData.cover_page
      });

      toast({
        title: 'Fax sent successfully',
        description: `Fax ID: ${result.fax_id}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form
      setFormData({
        title: '',
        subject: config?.default_subject || '',
        comments: config?.default_comments || '',
        cover_page: config?.default_cover_page || 'Standard',
        recipient_name: '',
        organization: '',
        to_fax_number: '',
        template_type: 'custom'
      });
      setFiles([]);
      setSelectedTemplate(null);

    } catch (error) {
      console.error('Fax sending error:', error);
      toast({
        title: 'Fax sending failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Loading fax editor...</Text>
        </VStack>
      </Center>
    );
  }

  return (
    <Grid templateColumns="1fr 1fr" gap={6} h="full">
      {/* Left Panel - Editor */}
      <GridItem>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <Box>
            <Text fontSize="xl" fontWeight="bold" mb={2}>
              Fax Document Editor
            </Text>
            <Text color="gray.600">
              Create and edit fax documents with preview
            </Text>
          </Box>

          {/* Templates */}
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Text fontWeight="bold">Templates</Text>
                <Badge colorScheme="blue">{templates.length} templates</Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                {templates.map((template) => (
                  <Box
                    key={template.id}
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    onClick={() => loadTemplate(template)}
                  >
                    <HStack justify="space-between">
                      <Text fontWeight="medium">{template.title}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {new Date(template.created_at).toLocaleDateString()}
                      </Text>
                    </HStack>
                  </Box>
                ))}
                {templates.length === 0 && (
                  <Text color="gray.500" textAlign="center" py={4}>
                    No templates available
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Form */}
          <Card>
            <CardHeader>
              <Text fontWeight="bold">Fax Details</Text>
            </CardHeader>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <FormControl>
                  <FormLabel>Title</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Document title"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Recipient Fax Number</FormLabel>
                  <Input
                    value={formData.to_fax_number}
                    onChange={(e) => handleInputChange('to_fax_number', e.target.value)}
                    placeholder="1234567890"
                    type="tel"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Recipient Name</FormLabel>
                  <Input
                    value={formData.recipient_name}
                    onChange={(e) => handleInputChange('recipient_name', e.target.value)}
                    placeholder="Recipient name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Organization</FormLabel>
                  <Input
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    placeholder="Organization name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Subject</FormLabel>
                  <Input
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Fax subject"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Comments</FormLabel>
                  <Textarea
                    value={formData.comments}
                    onChange={(e) => handleInputChange('comments', e.target.value)}
                    placeholder="Additional comments"
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Cover Page</FormLabel>
                  <Select
                    value={formData.cover_page}
                    onChange={(e) => handleInputChange('cover_page', e.target.value)}
                  >
                    <option value="Basic">Basic</option>
                    <option value="Standard">Standard</option>
                    <option value="Company">Company</option>
                    <option value="Personal">Personal</option>
                  </Select>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* File Upload */}
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Text fontWeight="bold">Files</Text>
                <Button
                  size="sm"
                  leftIcon={<Icon as={FaUpload} />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Add Files
                </Button>
              </HStack>
            </CardHeader>
            <CardBody>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
              
              <VStack spacing={2} align="stretch">
                {files.map((file, index) => (
                  <Box
                    key={index}
                    p={3}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                  >
                    <HStack justify="space-between">
                      <VStack align="start" spacing={1}>
                        <Text fontSize="sm" fontWeight="medium">{file.name}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {(file.size / 1024).toFixed(1)} KB
                        </Text>
                      </VStack>
                      <Button
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => removeFile(index)}
                      >
                        <Icon as={FaTrash} />
                      </Button>
                    </HStack>
                  </Box>
                ))}
                {files.length === 0 && (
                  <Text color="gray.500" textAlign="center" py={4}>
                    No files selected
                  </Text>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Actions */}
          <HStack spacing={4}>
            <Button
              leftIcon={<Icon as={FaEdit} />}
              onClick={saveTemplate}
              variant="outline"
              flex={1}
            >
              Save Template
            </Button>
            <Button
              leftIcon={<Icon as={FaFax} />}
              onClick={handleSendFax}
              isLoading={sending}
              loadingText="Sending..."
              colorScheme="blue"
              flex={1}
            >
              Send Fax
            </Button>
          </HStack>
        </VStack>
      </GridItem>

      {/* Right Panel - Preview */}
      <GridItem>
        <Card h="full">
          <CardHeader>
            <HStack justify="space-between">
              <Text fontWeight="bold">Preview</Text>
              <Button
                size="sm"
                variant="outline"
                leftIcon={<Icon as={previewMode ? FaEdit : FaEye} />}
                onClick={() => setPreviewMode(!previewMode)}
              >
                {previewMode ? 'Edit' : 'Preview'}
              </Button>
            </HStack>
          </CardHeader>
          <CardBody>
            <VStack spacing={4} align="stretch" h="full">
              {/* Cover Page Preview */}
              <Box
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
                bg="white"
                minH="300px"
              >
                <Text fontSize="lg" fontWeight="bold" mb={4}>
                  {formData.cover_page} Cover Page
                </Text>
                
                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="sm" color="gray.600">To:</Text>
                    <Text fontWeight="medium">{formData.recipient_name || '[Recipient Name]'}</Text>
                    {formData.organization && (
                      <Text fontSize="sm" color="gray.500">{formData.organization}</Text>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontSize="sm" color="gray.600">Subject:</Text>
                    <Text fontWeight="medium">{formData.subject || '[Subject]'}</Text>
                  </Box>

                  <Divider />

                  {formData.comments && (
                    <Box>
                      <Text fontSize="sm" color="gray.600">Comments:</Text>
                      <Text fontSize="sm" whiteSpace="pre-wrap">{formData.comments}</Text>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Text fontSize="sm" color="gray.600">Files to be sent:</Text>
                    {files.map((file, index) => (
                      <Text key={index} fontSize="sm" ml={2}>
                        • {file.name} ({(file.size / 1024).toFixed(1)} KB)
                      </Text>
                    ))}
                    {files.length === 0 && (
                      <Text fontSize="sm" color="gray.500" ml={2}>
                        • No files selected
                      </Text>
                    )}
                  </Box>
                </VStack>
              </Box>

              {/* File Previews */}
              {files.length > 0 && (
                <Box>
                  <Text fontWeight="bold" mb={2}>File Previews</Text>
                  <VStack spacing={2} align="stretch">
                    {files.map((file, index) => (
                      <Box
                        key={index}
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        bg="gray.50"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" fontWeight="medium">{file.name}</Text>
                            <Text fontSize="xs" color="gray.500">
                              {file.type} • {(file.size / 1024).toFixed(1)} KB
                            </Text>
                          </VStack>
                          <Button size="sm" variant="ghost" leftIcon={<Icon as={FaDownload} />}>
                            Download
                          </Button>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
            </VStack>
          </CardBody>
        </Card>
      </GridItem>
    </Grid>
  );
}
